/**
 * Company Types
 *
 * Tipos para el manejo de compañías cliente en OWLY Platform
 */

export enum CompanyStatus {
  ACTIVE = 'active',
  TRIAL = 'trial',
  SUSPENDED = 'suspended',
  CANCELLED = 'cancelled',
}

export enum CompanyPlan {
  STARTER = 'starter',
  PROFESSIONAL = 'professional',
  ENTERPRISE = 'enterprise',
  CUSTOM = 'custom',
}

/**
 * OWLY CRM es un CRM vertical especializado en el sector inmobiliario
 * Todas las compañías cliente son inmobiliarias/constructoras
 */
export enum CompanyIndustry {
  REAL_ESTATE = 'real_estate',
}

/**
 * Información de facturación de una compañía
 */
export interface BillingInfo {
  /** Plan contratado */
  plan: CompanyPlan;

  /** Precio mensual en CLP */
  monthlyPrice: number;

  /** Número de usuarios contratados */
  userSeats: number;

  /** Fecha de inicio de suscripción */
  subscriptionStartDate: string;

  /** Fecha de próxima facturación */
  nextBillingDate: string;

  /** Método de pago */
  paymentMethod: 'credit_card' | 'bank_transfer' | 'invoice';

  /** Estado de pago */
  paymentStatus: 'current' | 'overdue' | 'pending';

  /** Monto pendiente (si hay) */
  outstandingBalance?: number;
}

/**
 * Métricas de uso de una compañía
 */
export interface CompanyUsageMetrics {
  /** Número de usuarios activos */
  activeUsers: number;

  /** Número total de leads */
  totalLeads: number;

  /** Número de leads creados este mes */
  leadsThisMonth: number;

  /** Número total de clientes */
  totalClients: number;

  /** Número de proyectos activos */
  activeProjects: number;

  /** Último login (fecha ISO) */
  lastLogin: string;

  /** Interacciones totales (mensajes, llamadas, etc.) */
  totalInteractions: number;
}

/**
 * Representación de una compañía cliente
 */
export interface Company {
  /** ID único de la compañía */
  id: string;

  /** Nombre de la compañía */
  name: string;

  /** Logo de la compañía (URL) */
  logo?: string;

  /** Industria */
  industry: CompanyIndustry;

  /** Estado de la compañía */
  status: CompanyStatus;

  /** Información de facturación */
  billing: BillingInfo;

  /** Métricas de uso */
  metrics: CompanyUsageMetrics;

  /** Fecha de creación */
  createdAt: string;

  /** Fecha de última actualización */
  updatedAt: string;

  /** Usuario OWLY asignado (Sales o Support) */
  assignedOwlyUser?: {
    id: string;
    name: string;
    role: string;
  };

  /** Notas internas */
  internalNotes?: string;

  /** Features habilitados */
  enabledFeatures: string[];

  // ========================================
  // RELACIONES (Cross-Module References)
  // ========================================

  /** IDs de los mercados donde opera la compañía (Many-to-Many) */
  marketIds: string[];

  /** IDs de los proyectos que pertenecen a esta compañía (One-to-Many) */
  projectIds: string[];
}

/**
 * Resumen ejecutivo de todas las compañías
 */
export interface CompaniesSummary {
  /** Total de compañías */
  total: number;

  /** Compañías activas */
  active: number;

  /** Compañías en trial */
  trial: number;

  /** Compañías suspendidas */
  suspended: number;

  /** Revenue mensual recurrente (MRR) */
  monthlyRecurringRevenue: number;

  /** Revenue anual recurrente (ARR) */
  annualRecurringRevenue: number;

  /** Tasa de crecimiento mes a mes (%) */
  growthRate: number;

  /** Número de nuevas compañías este mes */
  newCompaniesThisMonth: number;

  /** Churn rate (%) */
  churnRate: number;
}
